import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import { getMyBankSlipsAPI } from "../../../services/portal/student/bankTransferService";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import { getMySeminarPaymentsByStatusAPI } from "../../../services/portal/student/studentSeminarService";
import { getMyCoursePaymentsByStatusAPI } from "../../../services/portal/student/studentCourseService";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import {
  formatDate,
  formatAmount,
  tableHeaderColor,
  getSeminarPaymentType,
} from "../../../utility/Common";

const PaymentHistoryPage = () => {
  const [loading, setLoading] = useState(false);
  const [filteredBankTransfers, setFilteredBankTransfers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("0");
  const [selectedStatus, setSelectedStatus] = useState("1");

  const [seminarLoading, setSeminarLoading] = useState(false);
  const [seminarPayments, setSeminarPayments] = useState([]);
  const [selectedSeminarStatus, setSelectedSeminarStatus] = useState("1");

  const [courseLoading, setCourseLoading] = useState(false);
  const [coursePayments, setCoursePayments] = useState([]);
  const [selectedCourseStatus, setSelectedCourseStatus] = useState("1");

  useEffect(() => {
    loadClasses();
  }, []);

  const loadBankTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyBankSlipsAPI(
        selectedClassId,
        selectedStatus,
        "''",
      );
      if (res && res.IsSuccessful && res.Data) {
        setFilteredBankTransfers(res.Data || []);
      } else {
        setFilteredBankTransfers([]);
      }
    } catch (error) {
      console.error("Error loading bank transfers:", error);
      showErrorToastMsg("Failed to load bank transfers");
      setFilteredBankTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus]);

  const loadSeminarPayments = useCallback(async () => {
    setSeminarLoading(true);
    try {
      const res = await getMySeminarPaymentsByStatusAPI(selectedSeminarStatus);
      if (res && res.IsSuccessful && res.Data) {
        setSeminarPayments(res.Data || []);
      } else {
        setSeminarPayments([]);
      }
    } catch (error) {
      console.error("Error loading seminar payments:", error);
      showErrorToastMsg("Failed to load seminar payments");
      setSeminarPayments([]);
    } finally {
      setSeminarLoading(false);
    }
  }, [selectedSeminarStatus]);

  const loadCoursePayments = useCallback(async () => {
    setCourseLoading(true);
    try {
      const res = await getMyCoursePaymentsByStatusAPI(selectedCourseStatus);
      if (res && res.IsSuccessful && res.Data) {
        setCoursePayments(res.Data || []);
      } else {
        setCoursePayments([]);
      }
    } catch (error) {
      console.error("Error loading course payments:", error);
      showErrorToastMsg("Failed to load course payments");
      setCoursePayments([]);
    } finally {
      setCourseLoading(false);
    }
  }, [selectedCourseStatus]);

  useEffect(() => {
    loadBankTransfers();
  }, [loadBankTransfers]);

  useEffect(() => {
    loadSeminarPayments();
  }, [loadSeminarPayments]);

  useEffect(() => {
    loadCoursePayments();
  }, [loadCoursePayments]);

  const loadClasses = async () => {
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    }
  };

  const handleClassFilterChange = (e) => {
    setSelectedClassId(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleSeminarStatusFilterChange = (e) => {
    setSelectedSeminarStatus(e.target.value);
  };

  const getStatusBadge = (status) => {
    const statusNum = Number(status);
    if (statusNum === 2) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          Approved
        </span>
      );
    } else if (statusNum === 4) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          Rejected
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    }
  };

  return (
    <DashboardLayout
      title="Bank Transfer Management"
      subtitle="View and manage all bank transfers"
    >
      <div>
        {/* Filter Section */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Filter by Class:
              </label>
              <select
                value={selectedClassId}
                onChange={handleClassFilterChange}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
              >
                <option value="0">All Classes</option>
                {classes.map((classItem) => (
                  <option key={classItem.ClassId} value={classItem.ClassId}>
                    {classItem.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Filter by Status:
              </label>
              <select
                value={selectedStatus}
                onChange={handleStatusFilterChange}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
              >
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="4">Rejected</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing {filteredBankTransfers.length} transfer(s)
            </div>
          </div>
        </div>

        {/* Class Bank Transfers Table */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              Bank Transfers List | ගෙවීම් විස්තර
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : filteredBankTransfers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No bank transfers found.
                    </td>
                  </tr>
                ) : (
                  filteredBankTransfers.map((transfer) => {
                    return (
                      <tr
                        key={transfer.BankSlipId}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transfer.User?.FirstName +
                            " " +
                            transfer.User?.LastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.Class?.Name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.Date ? formatDate(transfer.Date) : ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transfer.Amount ? formatAmount(transfer.Amount) : ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.Number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {getStatusBadge(transfer.Status)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Seminar Payments Filter */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Seminar Payment Status:
              </label>
              <select
                value={selectedSeminarStatus}
                onChange={handleSeminarStatusFilterChange}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
              >
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="4">Rejected</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing {seminarPayments.length} payment(s)
            </div>
          </div>
        </div>

        {/* Seminar Payments Table */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden" id="seminar-payments">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              Seminar Payments | සම්මන්ත්‍රණ ගෙවීම් විස්තර
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seminar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {seminarLoading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : seminarPayments.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No seminar payments found.
                    </td>
                  </tr>
                ) : (
                  seminarPayments.map((payment) => (
                    <tr
                      key={payment.Id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment?.Title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getSeminarPaymentType(payment.Type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Date ? formatDate(payment.Date) : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.Fee ? formatAmount(payment.Fee) : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {getStatusBadge(payment.Status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Course Payments Filter */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Course Payment Status:
              </label>
              <select
                value={selectedCourseStatus}
                onChange={(e) => setSelectedCourseStatus(e.target.value)}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
              >
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="4">Rejected</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              Showing {coursePayments.length} payment(s)
            </div>
          </div>
        </div>

        {/* Course Payments Table */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
          <div className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}>
            <h4 className="text-md font-medium text-white">
              Course Payments | කෝස ගෙවීම් විස්තර
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {courseLoading ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : coursePayments.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No course payments found.
                    </td>
                  </tr>
                ) : (
                  coursePayments.map((payment) => (
                    <tr key={payment.Id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment?.Title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getSeminarPaymentType(payment.Type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Date ? formatDate(payment.Date) : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.Fee ? formatAmount(payment.Fee) : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {getStatusBadge(payment.Status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaymentHistoryPage;
